<script>
    import { onMount } from 'svelte';
    import { push, location } from 'svelte-spa-router';
    import axios from '@edu-quizz/shared/lib/axios';

    // view: 'list' | 'new' | 'thread'
    let view = 'list';
    let reports = [];
    let loading = false;
    let sending = false;
    let error = '';
    let newDescription = '';
    let selectedReport = null;
    let newMessage = '';
    let threadEl;

    async function loadReports() {
        loading = true;
        error = '';
        try {
            const res = await axios.get('/api/v1/bug-reports');
            reports = res.data;
        } catch (e) {
            error = 'Impossible de charger les signalements.';
        } finally {
            loading = false;
        }
    }

    async function submitNew() {
        if (!newDescription.trim()) return;
        sending = true;
        error = '';
        try {
            const res = await axios.post('/api/v1/bug-reports', { description: newDescription });
            newDescription = '';
            reports = [res.data, ...reports];
            await openThread(res.data);
        } catch (e) {
            error = "Erreur lors de l'envoi.";
        } finally {
            sending = false;
        }
    }

    async function sendMessage() {
        if (!newMessage.trim()) return;
        sending = true;
        error = '';
        try {
            const res = await axios.post(`/api/v1/bug-reports/${selectedReport.id}/messages`, { body: newMessage });
            selectedReport.messages = [...selectedReport.messages, res.data];
            newMessage = '';
            setTimeout(() => threadEl?.scrollTo({ top: threadEl.scrollHeight, behavior: 'smooth' }), 50);
        } catch (e) {
            error = "Erreur lors de l'envoi.";
        } finally {
            sending = false;
        }
    }

    async function openThread(report) {
        selectedReport = report;
        view = 'thread';
        if (report.has_unread_response) {
            try {
                await axios.post(`/api/v1/bug-reports/${report.id}/mark-read`);
                report.has_unread_response = false;
                reports = reports;
            } catch (_) {}
        }
        setTimeout(() => threadEl?.scrollTo({ top: threadEl.scrollHeight }), 50);
    }

    function formatDate(dateStr) {
        return new Date(dateStr).toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    }

    onMount(() => {
        if ($location === '/bug-reports/new') view = 'new';
        loadReports();
    });
</script>

<div class="max-w-lg mx-auto px-4 py-6">

    <!-- Header -->
    <div class="flex items-center gap-3 mb-6">
        {#if view !== 'list'}
            <button class="btn btn-ghost btn-sm btn-circle" on:click={() => { view = 'list'; selectedReport = null; error = ''; }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
        {:else}
            <button class="btn btn-ghost btn-sm btn-circle" on:click={() => push('/')}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
        {/if}
        <h1 class="text-xl font-bold">
            {#if view === 'new'}Signaler un problème
            {:else if view === 'thread'}Conversation
            {:else}Mes signalements
            {/if}
        </h1>
    </div>

    {#if error}
        <div class="alert alert-error mb-4 text-sm py-2">{error}</div>
    {/if}

    <!-- Vue : nouveau signalement -->
    {#if view === 'new'}
        <div class="space-y-4">
            <textarea
                class="textarea textarea-bordered w-full"
                rows="6"
                placeholder="Décris le problème rencontré..."
                bind:value={newDescription}
                autofocus
            ></textarea>
            <div class="flex gap-3">
                <button class="btn btn-ghost flex-1" on:click={() => { view = 'list'; newDescription = ''; }}>Annuler</button>
                <button
                    class="btn btn-primary flex-1"
                    disabled={sending || !newDescription.trim()}
                    on:click={submitNew}
                >
                    {#if sending}<span class="loading loading-spinner loading-xs mr-1"></span>{/if}
                    Envoyer
                </button>
            </div>
        </div>

    <!-- Vue : fil de messages -->
    {:else if view === 'thread' && selectedReport}
        <div class="flex flex-col h-[calc(100vh-12rem)]">
            <div class="mb-2 flex items-center gap-2">
                <span class="badge badge-xs {selectedReport.status === 'open' ? 'badge-warning' : 'badge-success'}">
                    {selectedReport.status === 'open' ? 'Ouvert' : 'Fermé'}
                </span>
                <span class="text-xs text-base-content/50">{formatDate(selectedReport.created_at)}</span>
            </div>

            <!-- Messages -->
            <div class="flex-1 overflow-y-auto space-y-3 py-2" bind:this={threadEl}>
                {#each selectedReport.messages as msg}
                    <div class="flex {msg.is_admin ? 'justify-end' : 'justify-start'}">
                        <div class="max-w-xs rounded-2xl px-4 py-2 text-sm
                            {msg.is_admin
                                ? 'bg-primary text-primary-content rounded-br-sm'
                                : 'bg-base-200 text-base-content rounded-bl-sm'}">
                            <div class="text-xs opacity-60 mb-1">
                                {msg.is_admin ? 'Admin' : 'Toi'} · {formatDate(msg.created_at)}
                            </div>
                            <p class="whitespace-pre-wrap">{msg.body}</p>
                        </div>
                    </div>
                {/each}
            </div>

            <!-- Champ de réponse -->
            {#if selectedReport.status === 'open'}
                <div class="flex gap-2 pt-3 border-t border-base-300">
                    <input
                        type="text"
                        class="input input-bordered flex-1 text-sm"
                        placeholder="Ajouter un message..."
                        bind:value={newMessage}
                        on:keydown={e => e.key === 'Enter' && sendMessage()}
                    />
                    <button class="btn btn-primary" disabled={sending || !newMessage.trim()} on:click={sendMessage}>
                        {#if sending}
                            <span class="loading loading-spinner loading-xs"></span>
                        {:else}
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                        {/if}
                    </button>
                </div>
            {:else}
                <p class="text-center text-sm text-base-content/50 pt-3">Ce signalement est fermé.</p>
            {/if}
        </div>

    <!-- Vue : liste -->
    {:else}
        {#if loading}
            <div class="flex justify-center py-16"><span class="loading loading-spinner loading-lg"></span></div>
        {:else}
            <div class="space-y-3 mb-6">
                {#each reports as report}
                    <button
                        class="w-full text-left rounded-xl border p-4 hover:bg-base-200 transition-colors
                            {report.has_unread_response ? 'border-primary bg-primary/5' : 'border-base-300'}"
                        on:click={() => openThread(report)}
                    >
                        <div class="flex items-center justify-between gap-2 mb-2">
                            <span class="text-xs text-base-content/50">{formatDate(report.created_at)}</span>
                            <div class="flex items-center gap-1">
                                {#if report.has_unread_response}
                                    <span class="badge badge-primary badge-sm">Réponse reçue</span>
                                {/if}
                                <span class="badge badge-xs {report.status === 'open' ? 'badge-warning' : 'badge-success'}">
                                    {report.status === 'open' ? 'Ouvert' : 'Fermé'}
                                </span>
                            </div>
                        </div>
                        <p class="text-sm line-clamp-2">{report.description}</p>
                        <p class="text-xs text-base-content/40 mt-1">{report.messages?.length ?? 0} message(s)</p>
                    </button>
                {:else}
                    <p class="text-center text-base-content/50 py-12">Aucun signalement pour l'instant.</p>
                {/each}
            </div>

            <button class="btn btn-primary w-full" on:click={() => { view = 'new'; error = ''; }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Nouveau signalement
            </button>
        {/if}
    {/if}
</div>
